// React
import React, { useRef, useState } from 'react';

// Refine
import { useNotification, useTranslation } from '@refinedev/core';

// Mantine
import { useDocumentTitle } from '@refinedev/react-router-v6';
import type {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableOptions,
} from 'mantine-react-table';

// Custom Components
import { BillingDataGridWrapper } from 'components/common/BillingDataGridWrapper';
import { TableProgressBar, TableAlertBanner } from 'components/common';
import { RemittanceToolBar } from './components/RemittanceToolBar';

// Columns
import { RemittanceColumns } from 'columns/Remittance';

// Hooks
import { useIsAdmin, useCanModify } from 'hooks';

// Utils
import { uploadFileToS3 } from 'utils/fileUpload';

// Interfaces
import type { Remittance, Provider } from 'interfaces';
import type { UseMutationResult } from '@tanstack/react-query';
import { useCurrentUserStore } from 'store/UserStore';
import { useCurrentSiteStore } from 'store/SiteStore';
import type { ProcessRemittanceResponse, RemittanceType } from 'api/action';

interface RemittanceManagementProps {
  remittance: Remittance[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  errorMessage: string;
  processRemittance: UseMutationResult<ProcessRemittanceResponse, Error>;
  siteProviders: Provider[];
}
import { RemittanceRowActions } from './components/RemittanceRowActions';

type RemittanceUploadType = 'REM' | 'P4' | 'RETURNS';
type RemittanceUpload = {
  file: File;
  key: string;
};

export const RemittanceManagement: React.FC<RemittanceManagementProps> = ({
  remittance,
  isLoading,
  isError,
  isFetching,
  errorMessage,
  processRemittance,
}) => {
  const { translate } = useTranslation();
  const title = translate('pages:remittances.label');
  useDocumentTitle(`${title} | QBilling`);
  const isAdmin = useIsAdmin();
  const { canModify } = useCanModify('remittances');
  const { autoRemit } = useCurrentSiteStore();
  const { open } = useNotification();
  const { id: userID } = useCurrentUserStore();
  const { id: siteID, siteNumber, province } = useCurrentSiteStore();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const openRef = useRef<() => void>(null);

  const [remittanceFile, setRemittanceFile] = useState<File | undefined>();
  const [p4File, setP4File] = useState<File | undefined>();
  const [returnsFile, setReturnsFile] = useState<File | undefined>();
  const [payor, setPayor] = useState<RemittanceType>(() => {
    if (province === 'BC') return 'BC';
    if (province === 'SK') return 'SK';
    if (province === 'MB' && !autoRemit) return 'MB';
    return 'MSH';
  });

  const remittanceBucket = `remittance-files-${
    import.meta.env.VITE_ACCOUNT_ID
  }`;

  const formatFileName = (
    file: File | undefined,
    fileType: RemittanceUploadType
  ): string | undefined => {
    if (!file?.name) {
      return undefined;
    }
    if (payor === 'BC') {
      return `${siteID}/BC/uploads/${file.name}`;
    }
    const formatSiteNum = String(siteNumber);
    return `${formatSiteNum}/${fileType}/${file.name}`;
  };

  const createUpload = (
    file: File | undefined,
    fileType: RemittanceUploadType
  ): RemittanceUpload | undefined => {
    const key = formatFileName(file, fileType);
    return file && key ? { file, key } : undefined;
  };

  const getRemittanceUpload = () =>
    payor === 'MB' || payor === 'BC'
      ? createUpload(remittanceFile, 'REM')
      : undefined;

  const getIncomeUpload = () =>
    payor === 'SK' || payor === 'BC' ? undefined : createUpload(p4File, 'P4');

  const getReturnUpload = () =>
    payor === 'SK' ? createUpload(returnsFile, 'RETURNS') : undefined;

  const uploadRemittanceFiles = async (
    paymentUpload: RemittanceUpload,
    remittanceUpload?: RemittanceUpload
  ) => {
    if (remittanceUpload) {
      await uploadFileToS3([remittanceUpload.file], remittanceBucket, [
        remittanceUpload.key,
      ]);
    }
    await uploadFileToS3([paymentUpload.file], remittanceBucket, [
      paymentUpload.key,
    ]);
  };

  const handleSubmitFiles = async () => {
    try {
      setIsUploading(true);
      const incomeUpload = getIncomeUpload();
      const returnUpload = getReturnUpload();
      const remittanceUpload = getRemittanceUpload();
      const paymentUpload =
        payor === 'BC' ? remittanceUpload : returnUpload ?? incomeUpload;

      if (!paymentUpload || (payor === 'MB' && !remittanceUpload)) {
        throw new Error(translate('pages:remittances.upload.requiredFiles'));
      }

      await uploadRemittanceFiles(
        paymentUpload,
        payor === 'MB' ? remittanceUpload : undefined
      );
      await processRemittance.mutateAsync({
        remittanceFile: remittanceUpload?.key,
        incomeFile: incomeUpload?.key,
        returnFile: returnUpload?.key,
        siteID,
        userID,
        type: payor,
      });
    } catch (error) {
      open?.({
        message:
          error instanceof Error
            ? error.message
            : translate('pages:remittances.upload.failed'),
        type: 'error',
      });
    } finally {
      setOpenModal(false);
      setIsUploading(false);
    }
  };

  const data: Remittance[] = remittance ?? [];
  const columns: MRT_ColumnDef<Remittance>[] = RemittanceColumns();

  const tableOptions: MRT_TableOptions<Remittance> = {
    data,
    columns,
    initialState: {
      sorting: [
        {
          desc: true,
          id: 'parsedAt',
        },
      ],
    },
    state: {
      showLoadingOverlay: isLoading || isFetching,
      showAlertBanner: isError,
      showProgressBars: isFetching || isLoading,
      columnPinning: { left: ['mrt-row-actions'] },
      columnSizing: { 'mrt-row-actions': 12 },
    },
    enableFullScreenToggle: false,
    enableEditing: isAdmin,
    enableColumnResizing: false,
    enableColumnPinning: true,
    mantineLoadingOverlayProps: {},
    mantineToolbarAlertBannerProps() {
      return TableAlertBanner({ isError, message: errorMessage });
    },
    mantineProgressProps({ isTopToolbar }) {
      return TableProgressBar({ isTopToolbar });
    },
    renderTopToolbarCustomActions: () =>
      RemittanceToolBar({
        openModal,
        setOpenModal,
        openRef,
        remittanceFile,
        setRemittanceFile,
        handleSubmitFiles,
        p4File,
        setP4File,
        returnsFile,
        setReturnsFile,
        payor,
        setPayor,
        isUploading,
      }),
    enableRowActions: true,
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 12,
        minSize: 12,
        maxSize: 12,
        Cell: ({ row }: { row: MRT_Row<Remittance> }) => (
          <RemittanceRowActions row={row} canModify={canModify} />
        ),
      },
    },
  };

  return (
    <BillingDataGridWrapper
      title={title}
      dataTestID="remittanceManagement"
      tableOptions={tableOptions}
    />
  );
};
